import { useState, useRef, useCallback } from 'react';
import { useOkapiKy } from '@folio/stripes/core';

const POLL_INTERVAL_MS = 2000;

/**
 * Hook that manages the full async export lifecycle:
 *   idle → preparing → running (polling) → ready → downloading → complete
 *
 * Stages map to ExportProgressModal states — no changes needed there.
 */
export const useExcelExport = () => {
  const okapiKy  = useOkapiKy();
  const pollTimer = useRef(null);

  const [exportStage,  setExportStage]  = useState('idle');
  const [exportError,  setExportError]  = useState(null);
  const [exportJobId,  setExportJobId]  = useState(null);
  const [exportProgress, setExportProgress] = useState(0);
  const [jobFormat,    setJobFormat]    = useState('excel');
  const [jobReportId,  setJobReportId]  = useState(null);

  const stopPolling = () => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  };

  const pollJobStatus = useCallback((jobId) => {
    stopPolling();
    pollTimer.current = setInterval(async () => {
      try {
        const job = await okapiKy.get(`report-export/jobs/${jobId}`).json();
        setExportProgress(job.progress || 0);

        if (job.status === 'completed') {
          stopPolling();
          setExportStage('ready');
        } else if (job.status === 'failed' || job.status === 'expired') {
          stopPolling();
          setExportError(job.errorMessage || 'Export failed');
          setExportStage('idle');
        }
        // 'pending' | 'running' — keep polling
      } catch (e) {
        stopPolling();
        setExportError(e.message);
        setExportStage('idle');
      }
    }, POLL_INTERVAL_MS);
  }, [okapiKy]);

  /** Start export job for a saved report. */
  const prepareExport = useCallback(async (reportId, format = 'excel', displayMode = 'table', displayConfig = {}) => {
    if (!reportId) { setExportError('No reportId provided'); return; }

    setExportStage('preparing');
    setExportError(null);
    setExportProgress(0);
    setJobFormat(format);
    setJobReportId(reportId);

    try {
      const res = await okapiKy
        .post(`report-export/${reportId}`, { json: { format, displayMode, displayConfig } })
        .json();

      setExportJobId(res.jobId);
      setExportStage('running');
      pollJobStatus(res.jobId);
    } catch (e) {
      setExportError(e.message);
      setExportStage('idle');
    }
  }, [okapiKy, pollJobStatus]);

  /** Trigger the actual file download once job is complete. */
  const triggerDownload = useCallback(async () => {
    if (!exportJobId) return;

    setExportStage('downloading');
    try {
      const response = await okapiKy
        .get(`report-export/download/${exportJobId}`)
        .blob();

      const job = await okapiKy
        .get(`report-export/jobs/${exportJobId}`)
        .json().catch(() => ({}));

      const reportName = (job.reportName || 'report').replace(/[^a-zA-Z0-9\-_]/g, '_');
      const ext = jobFormat === 'pdf' ? '.pdf' : jobFormat === 'csv' ? '.csv' : '.xlsx';
      const filename = `FOLIO_${reportName}_${new Date().toISOString().slice(0, 10)}${ext}`;

      const url  = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href     = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Cleanup job on server
      okapiKy.delete(`report-export/jobs/${exportJobId}`).catch(() => {});

      setExportStage('complete');
    } catch (e) {
      setExportError(e.message);
      setExportStage('ready'); // allow retry
    }
  }, [okapiKy, exportJobId, jobFormat]);

  const resetExport = useCallback(() => {
    stopPolling();
    setExportStage('idle');
    setExportError(null);
    setExportJobId(null);
    setExportProgress(0);
  }, []);

  return {
    prepareExport,
    triggerDownload,
    resetExport,
    exportStage,
    exportError,
    exportProgress,
    exportJobId,
    isExporting: exportStage === 'preparing' || exportStage === 'running' || exportStage === 'downloading',
    isReady:     exportStage === 'ready',
    isComplete:  exportStage === 'complete',
    clearError:  () => setExportError(null),
  };
};
